// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// The bar under the comment list while comments are ticked (Figma November
// 2026, frame 0b, "Bulk selection"): how many, Clear, and Relevant / Not
// relevant for all of them. Owner and Ready for review wait on their own work.
import { Button, Checkbox, Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { type ReviewBulkSelection } from "./useReviewBulkSelection";

import { useUI } from "../../contexts/ui";

export function AskV2ReviewBulkBar({
  selection,
  pending = false,
  quietMoves,
  onJudge,
}: {
  selection: ReviewBulkSelection;
  pending?: boolean;
  /** Ids being written; the list's moved toast skips them for this bar's one. */
  quietMoves: { readonly current: Set<string> };
  /** Resolves to the ids whose write failed. */
  onJudge: (
    commentIds: readonly string[],
    liked: boolean,
  ) => Promise<readonly string[]>;
}) {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const [busy, setBusy] = useState(false);
  const count = selection.selected.length;
  if (count === 0) return null;
  const judge = async (liked: boolean) => {
    const ids = selection.selected;
    for (const id of ids) quietMoves.current.add(id);
    setBusy(true);
    try {
      const failed = await onJudge(ids, liked);
      // why: a failed write never moves its comment, so nothing would take
      // its id off the quiet list.
      for (const id of failed) quietMoves.current.delete(id);
      const saved = ids.length - failed.length;
      if (saved > 0 && typeof notify === "function") {
        const text = t(
          liked
            ? "askV2.review.bulkMarkedRelevant"
            : "askV2.review.bulkMarkedNotRelevant",
          { count: saved },
        );
        // why: the shared snackbar sets aria-hidden, which drops the toast from the accessibility tree, and its prop type rejects data-testid on an object literal even though the aside spreads it.
        notify({
          title: text,
          variant: liked ? "success" : "default",
          "aria-label": text,
          "aria-hidden": false,
          "data-testid": "ask-v2-review-bulk-done",
        } as Parameters<typeof notify>[0]);
      }
      // why: the ticks stay on a failed write so Retry is one click; saved
      // ones leave the list, which drops their ticks.
      if (failed.length === 0) selection.clear();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      role="region"
      aria-label={t("askV2.review.bulkLabel")}
      className="mx-3 mb-3 rounded-lg border border-puntt-blue-6 bg-puntt-blue-2 p-3"
      data-testid="ask-v2-review-bulk-bar"
    >
      <Flex align="center" justify="between" gap="2">
        <Text
          as="p"
          size="2"
          className="text-puntt-neutral-gray-12"
          data-testid="ask-v2-review-bulk-count"
        >
          {t("askV2.review.bulkSelected", { count })}
        </Text>
        <Button
          type="button"
          size="1"
          variant="outline"
          color="gray"
          onClick={selection.clear}
          data-testid="ask-v2-review-bulk-clear"
        >
          {t("askV2.review.bulkClear")}
        </Button>
      </Flex>
      <Flex gap="2" mt="2">
        <Button
          type="button"
          size="1"
          variant="outline"
          color="gray"
          disabled={pending || busy}
          onClick={() => void judge(true)}
          data-testid="ask-v2-review-bulk-relevant"
        >
          {t("askV2.review.rowRelevant")}
        </Button>
        <Button
          type="button"
          size="1"
          variant="outline"
          color="gray"
          disabled={pending || busy}
          onClick={() => void judge(false)}
          data-testid="ask-v2-review-bulk-not-relevant"
        >
          {t("askV2.review.rowNotRelevant")}
        </Button>
      </Flex>
    </div>
  );
}

/** The box beside a comment card, or beside the tabs for every comment. */
export function AskV2ReviewBulkCheckbox({
  checked,
  label,
  "data-testid": testId,
  onChange,
}: {
  checked: boolean | "indeterminate";
  label: string;
  "data-testid": string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Checkbox
      size="2"
      className="mt-3 shrink-0"
      checked={checked}
      aria-label={label}
      data-testid={testId}
      onCheckedChange={(next) => onChange(next === true)}
    />
  );
}
